// hilfs methode um semi-private strings ins frontend zu bringen
// api keys sollten trozdem als public angesehen werden, wenn diese
// ueber hier ans frontend gebracht werden
import RequestManager from "more/RequestManager";

namespace Schluesseldienst {

    /**
     * Fragt einen Globalen Parameter ab, der für so halb geheime Werte (z.B. API-Keys für Iframes) gedacht ist.
     * ACHTUNG: Diese Funktion sollte NUR für Werte verwendet werden, die im Frontend benötigt werden,
     * aber nicht hochsensibel sind. Alle sensiblen Daten (z.B. Backend-API-Keys, Passwörter, Tokens)
     * MÜSSEN im Backend abgefragt und verarbeitet werden!
     *
     * Ebenfalls Wichtig: Der Parameter muss die Kategorie "ALLOW_PASSTHROUGH_FRONTEND" haben!
     *
     * @param requestManager RequestManager, der den Request ausführen soll
     * @param gpname Name des Globalen Parameters (Typen: "Verschluesselter Text", "Text lang" oder "Text")
     * @returns Den Inhalt des Parameters. Falls nicht gefunden, wird ein leerer String zurückgegeben.
    */
    export function fetchKeySync(requestManager: RequestManager, gpname: string, resultCallback: Function | ((result: string) => any)) {
        //simple wrapper
        fetchKeyAsync(requestManager, gpname).then((val: string) => {
            if (typeof resultCallback == 'function') {
                resultCallback(val);
            }
        });
    }

    /**
     * Fragt einen Globalen Parameter ab, der für so halb geheime Werte (z.B. API-Keys für Iframes) gedacht ist.
     * ACHTUNG: Diese Funktion sollte NUR für Werte verwendet werden, die im Frontend benötigt werden,
     * aber nicht hochsensibel sind. Alle sensiblen Daten (z.B. Backend-API-Keys, Passwörter, Tokens)
     * MÜSSEN im Backend abgefragt und verarbeitet werden!
     *
     * Ebenfalls Wichtig: Der Parameter muss die Kategorie "ALLOW_PASSTHROUGH_FRONTEND" haben!
     *
     * @param requestManager RequestManager, der den Request ausführen soll
     * @param gpname Name des Globalen Parameters (Typen: "Verschluesselter Text", "Text lang" oder "Text")
     * @returns Den Inhalt des Parameters. Falls nicht gefunden, wird ein leerer String zurückgegeben.
    */
    export async function fetchKeyAsync(requestManager: RequestManager, gpname: string): Promise<string> {
        if ( typeof window === 'undefined' || !window.crypto || !window.crypto.subtle ) {
            throw new Error("Crypto API not available in this browser!");
        }

        if (requestManager == null) {
            throw new Error("invalid request manager passed");
        }
        if (typeof requestManager.get != 'function') {
            throw new Error("invalid request manager passed");
        }
        if (!gpname || typeof gpname != 'string' || gpname.trim() === '') {
            throw new Error("Invalid or empty string passed for gpname");
        }


        // besser als decrypt key in plaintext in compiled js haben, aber auch nicht optimal
        const K:string = atob("UE05UQ==") + "T.A.w.6.w.s.X.n.B.D.6.u.H.X.f.S.S.3.Y.G.T".split(".").reverse().join('');

        let reqProm: Promise<{ key: string, salt: string }> = new Promise((resolve, reject) => {
            requestManager.get(window.appRoot + "Schluesseldienst/ausgespert?k=" + encodeURIComponent(gpname), (val: { key: string, salt: string }) => {
                resolve(val);
            }, (error) => {
                reject(error);
            });
        });

        let result: { key: string, salt: string };
        try {
            result = await reqProm;
        } catch (e) {
            console.error(e);
            throw e;
        }

        if (!result.key || !result.salt) {
            throw new Error("invalid server response received");
        }

        // wichtig, wenn key und result empty sind, dann war gp empty -> result ist empty
        if (result.key == "" && result.salt == "") {
            return "";
        }

        // Decrypt the key
        try {
            const decryptedKey = await decrypt(result.key, K, result.salt);
            return decryptedKey;
        } catch (e) {
            console.error("Decryption failed:", e);
            throw e;
        }
    }

    // AES decryption function to match the C# encryption
    async function decrypt(encryptedData: string, password: string, saltStr: string): Promise<string> {
        const iterations = 10000;
        const keySize = 32; // 256 bits / 8
        const ivSize = 16;  // 128 bits / 8

        // Convert inputs
        const salt = new TextEncoder().encode(saltStr);
        const encryptedBytes = base64ToArrayBuffer(encryptedData);
        const passwordBytes = new TextEncoder().encode(password);

        // Derive key and IV using PBKDF2 (same as Rfc2898DeriveBytes in C#)
        const keyMaterial = await crypto.subtle.importKey(
            'raw',
            passwordBytes,
            { name: 'PBKDF2' },
            false,
            ['deriveBits']
        );

        const derivedBits = await crypto.subtle.deriveBits(
            {
                name: 'PBKDF2',
                salt: salt,
                iterations: iterations,
                hash: 'SHA-1' // Rfc2898DeriveBytes uses SHA-1 by default
            },
            keyMaterial,
            (keySize + ivSize) * 8
        );

        const derivedArray = new Uint8Array(derivedBits);
        const key = derivedArray.slice(0, keySize);
        const iv = derivedArray.slice(keySize, keySize + ivSize);

        // Import the derived key for AES
        const aesKey = await crypto.subtle.importKey(
            'raw',
            key,
            { name: 'AES-CBC' },
            false,
            ['decrypt']
        );

        // Decrypt
        const decryptedArrayBuffer = await crypto.subtle.decrypt(
            {
                name: 'AES-CBC',
                iv: iv
            },
            aesKey,
            encryptedBytes
        );

        // Convert result back to string
        const decryptedString = new TextDecoder().decode(decryptedArrayBuffer);
        return decryptedString;
    }

    // Helper function to convert base64 to ArrayBuffer
    function base64ToArrayBuffer(base64: string): ArrayBuffer {
        const binaryString = atob(base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
    }
}

export { Schluesseldienst };
